import { useRef } from 'react';
import { useUnit } from 'effector-react';
import { button, useControls } from 'leva';
import {
  $splatTransform,
  $splatTransformInitial,
  $splatDebugVisible,
  initSplatTransform,
  updateSplatTransform,
  setSplatDebugVisible,
} from '../../state/splatTransform';
import { $devConfig } from '../../state/appConfig';

type ChangeContext = { initial?: boolean };

const RAD_TO_DEG = 180 / Math.PI;
const DEG_TO_RAD = Math.PI / 180;

export const SplatTransformPanel = () => {
  const { debugSplatTransform } = useUnit($devConfig);
  const transform = useUnit($splatTransform);
  const splatVisible = useUnit($splatDebugVisible);
  const isProgrammatic = useRef(false);

  const [, set] = useControls(
    'Splat Transform',
    () => ({
      'Show Splat': {
        value: splatVisible,
        onChange: (v: boolean, _: string, ctx: ChangeContext) => {
          if (ctx.initial) return;
          setSplatDebugVisible(v);
        },
      },
      'Pos X': {
        value: transform.position.x,
        min: -10, max: 10, step: 0.001,
        onChange: (v: number, _: string, ctx: ChangeContext) => {
          if (ctx.initial || isProgrammatic.current) return;
          updateSplatTransform({ position: { ...transform.position, x: v } });
        },
      },
      'Pos Y': {
        value: transform.position.y,
        min: -10, max: 10, step: 0.001,
        onChange: (v: number, _: string, ctx: ChangeContext) => {
          if (ctx.initial || isProgrammatic.current) return;
          updateSplatTransform({ position: { ...transform.position, y: v } });
        },
      },
      'Pos Z': {
        value: transform.position.z,
        min: -10, max: 10, step: 0.001,
        onChange: (v: number, _: string, ctx: ChangeContext) => {
          if (ctx.initial || isProgrammatic.current) return;
          updateSplatTransform({ position: { ...transform.position, z: v } });
        },
      },
      'Rot X (deg)': {
        value: transform.rotation.x * RAD_TO_DEG,
        min: -180, max: 180, step: 0.1,
        onChange: (v: number, _: string, ctx: ChangeContext) => {
          if (ctx.initial || isProgrammatic.current) return;
          updateSplatTransform({ rotation: { ...transform.rotation, x: v * DEG_TO_RAD } });
        },
      },
      'Rot Y (deg)': {
        value: transform.rotation.y * RAD_TO_DEG,
        min: -180, max: 180, step: 0.1,
        onChange: (v: number, _: string, ctx: ChangeContext) => {
          if (ctx.initial || isProgrammatic.current) return;
          updateSplatTransform({ rotation: { ...transform.rotation, y: v * DEG_TO_RAD } });
        },
      },
      'Rot Z (deg)': {
        value: transform.rotation.z * RAD_TO_DEG,
        min: -180, max: 180, step: 0.1,
        onChange: (v: number, _: string, ctx: ChangeContext) => {
          if (ctx.initial || isProgrammatic.current) return;
          updateSplatTransform({ rotation: { ...transform.rotation, z: v * DEG_TO_RAD } });
        },
      },
      'Scale X': {
        value: transform.scale.x,
        min: 0.001, max: 10, step: 0.001,
        onChange: (v: number, _: string, ctx: ChangeContext) => {
          if (ctx.initial || isProgrammatic.current) return;
          updateSplatTransform({ scale: { ...transform.scale, x: v } });
        },
      },
      'Scale Y': {
        value: transform.scale.y,
        min: 0.001, max: 10, step: 0.001,
        onChange: (v: number, _: string, ctx: ChangeContext) => {
          if (ctx.initial || isProgrammatic.current) return;
          updateSplatTransform({ scale: { ...transform.scale, y: v } });
        },
      },
      'Scale Z': {
        value: transform.scale.z,
        min: 0.001, max: 10, step: 0.001,
        onChange: (v: number, _: string, ctx: ChangeContext) => {
          if (ctx.initial || isProgrammatic.current) return;
          updateSplatTransform({ scale: { ...transform.scale, z: v } });
        },
      },
      'Copy Config': button(() => {
        const t = $splatTransform.getState();
        const fmt = (v: number) => Number(v.toFixed(4));
        const code = [
          '{',
          `  position: { x: ${fmt(t.position.x)}, y: ${fmt(t.position.y)}, z: ${fmt(t.position.z)} },`,
          `  rotation: { x: ${fmt(t.rotation.x)}, y: ${fmt(t.rotation.y)}, z: ${fmt(t.rotation.z)} },`,
          `  scale: { x: ${fmt(t.scale.x)}, y: ${fmt(t.scale.y)}, z: ${fmt(t.scale.z)} },`,
          '}',
        ].join('\n');
        navigator.clipboard.writeText(code);
        // eslint-disable-next-line no-console
        console.log('[SplatTransform] Copied:\n' + code);
      }),
      'Reset': button(() => {
        const initial = $splatTransformInitial.getState();
        isProgrammatic.current = true;
        set({
          'Pos X': initial.position.x,
          'Pos Y': initial.position.y,
          'Pos Z': initial.position.z,
          'Rot X (deg)': initial.rotation.x * RAD_TO_DEG,
          'Rot Y (deg)': initial.rotation.y * RAD_TO_DEG,
          'Rot Z (deg)': initial.rotation.z * RAD_TO_DEG,
          'Scale X': initial.scale.x,
          'Scale Y': initial.scale.y,
          'Scale Z': initial.scale.z,
        });
        isProgrammatic.current = false;
        initSplatTransform(initial);
      }),
    }),
    { collapsed: true, render: () => debugSplatTransform },
    [transform, splatVisible, debugSplatTransform],
  );

  return null;
};
